import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button } from '@ohif/ui-next';
import { bytesToMegabytes, MAX_TOTAL_SIZE_BYTES } from '../../utils/dicomIntake';

interface DropZoneProps {
  isIntaking: boolean;
  onFiles: (files: File[]) => void;
}

function openPicker(onFiles: (files: File[]) => void, directory: boolean): void {
  const input = document.createElement('input');

  input.type = 'file';
  input.multiple = true;

  if (directory) {
    input.webkitdirectory = true;
  }

  input.onchange = () => onFiles(Array.from(input.files ?? []));
  input.click();
}

export default function DropZone({ isIntaking, onFiles }: DropZoneProps) {
  const onDrop = useCallback((accepted: File[]) => onFiles(accepted), [onFiles]);

  const { getRootProps } = useDropzone({ onDrop, noClick: true, noKeyboard: true });

  return (
    <div
      {...getRootProps()}
      className="border-primary relative m-5 flex h-full flex-col items-center justify-center rounded-2xl border-[3px] border-dashed"
      data-cy="sycai-upload-dropzone"
    >
      {isIntaking && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50"
          data-cy="sycai-upload-intaking"
        >
          <div className="text-lg text-white">Extracting files...</div>
        </div>
      )}

      <div className="flex w-full flex-col items-center gap-6">
        <div className="flex gap-3">
          <Button
            variant="default"
            size="lg"
            className="text-white"
            dataCY="sycai-upload-add-files"
            onClick={() => openPicker(onFiles, false)}
          >
            Add File
          </Button>
          <Button
            variant="default"
            size="lg"
            className="text-white"
            dataCY="sycai-upload-add-folder"
            onClick={() => openPicker(onFiles, true)}
          >
            Add Folder
          </Button>
        </div>

        <div className="text-foreground pt-5">or drag images or folders here</div>
        <div className="text-muted-foreground pt-3 text-lg">
          (DICOM files, ZIP archives supported. Max total size of unzipped study:{' '}
          {bytesToMegabytes(MAX_TOTAL_SIZE_BYTES)}MB
        </div>
      </div>
    </div>
  );
}
